/**
 * FavoritesList Component
 * Container that renders PhraseCard list with search and empty state.
 */

import React, { useState } from 'react';
import { FavoritePhrase } from '../../types';
import { PhraseCard } from './PhraseCard';
import './FavoritesList.css';

interface FavoritesListProps {
  phrases: FavoritePhrase[];
  onSelect: (phrase: FavoritePhrase) => void;
  onEdit: (phrase: FavoritePhrase) => void;
  onDelete: (phrase: FavoritePhrase) => void;
  onAdd: () => void;
}

export const FavoritesList: React.FC<FavoritesListProps> = ({
  phrases,
  onSelect,
  onEdit,
  onDelete,
  onAdd,
}) => {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredPhrases = phrases.filter((phrase) => {
    if (!searchQuery) return true;

    const query = searchQuery.toLowerCase();
    return (
      phrase.label.toLowerCase().includes(query) ||
      phrase.text.toLowerCase().includes(query) ||
      phrase.tags.some((tag) => tag.toLowerCase().includes(query)) ||
      (phrase.targetLang && phrase.targetLang.toLowerCase().includes(query))
    );
  });

  return (
    <div className="favorites-list">
      <div className="favorites-list__header">
        <div className="favorites-list__search">
          <input
            type="text"
            className="favorites-list__search-input"
            placeholder="Search phrases..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            aria-label="Search favorite phrases"
          />
          {searchQuery && (
            <button
              className="favorites-list__search-clear"
              onClick={() => setSearchQuery('')}
              aria-label="Clear search"
            >
              x
            </button>
          )}
        </div>

        <button
          className="favorites-list__add-btn"
          onClick={onAdd}
          aria-label="Add new phrase"
        >
          + Add Phrase
        </button>
      </div>

      {filteredPhrases.length === 0 ? (
        <div className="favorites-list__empty">
          {searchQuery ? (
            <>
              <p>No phrases found matching "{searchQuery}"</p>
              <button
                className="favorites-list__clear-search"
                onClick={() => setSearchQuery('')}
              >
                Clear search
              </button>
            </>
          ) : (
            <>
              <p>No favorite phrases yet.</p>
              <p className="favorites-list__empty-hint">
                Save phrases you use often for quick one-tap translations.
              </p>
              <button
                className="favorites-list__empty-add-btn"
                onClick={onAdd}
              >
                Add Your First Phrase
              </button>
            </>
          )}
        </div>
      ) : (
        <div className="favorites-list__items">
          {filteredPhrases.map((phrase) => (
            <PhraseCard
              key={phrase.id}
              phrase={phrase}
              onClick={onSelect}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
};
